import { useState, useEffect, useCallback, useMemo } from "react";
import { useAuth, api } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  ShieldAlert, Plus, Pencil, Trash2, Users, RefreshCw, Lock, Unlock, Save,
} from "lucide-react";
import { toast } from "sonner";

export default function RolesPage() {
  const { user } = useAuth();
  const [roles, setRoles] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);   // role doc currently in editor
  const [creating, setCreating] = useState(null); // {label, description, base_role, permissions}
  const [deleting, setDeleting] = useState(null); // role doc being confirmed
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rRes, cRes] = await Promise.all([
        api.get("/roles"),
        api.get("/roles/permissions-catalog"),
      ]);
      setRoles(rRes.data);
      setCatalog(cRes.data.permissions);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load roles");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const sections = useMemo(() => {
    const map = new Map();
    catalog.forEach((p) => {
      if (!map.has(p.section)) map.set(p.section, []);
      map.get(p.section).push(p);
    });
    return Array.from(map.entries());
  }, [catalog]);

  const togglePerm = (current, key) => {
    const set = new Set(current.permissions || []);
    if (set.has(key)) set.delete(key);
    else set.add(key);
    return { ...current, permissions: Array.from(set) };
  };

  const toggleSection = (current, sectionKeys, allChecked) => {
    const set = new Set(current.permissions || []);
    sectionKeys.forEach((k) => allChecked ? set.delete(k) : set.add(k));
    return { ...current, permissions: Array.from(set) };
  };

  const saveRole = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const { data } = await api.put(`/roles/${editing.key}`, {
        label: editing.label,
        description: editing.description,
        permissions: editing.permissions,
      });
      toast.success(`Saved '${data.label}'`);
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const createRole = async () => {
    if (!creating?.label?.trim()) {
      toast.error("Label is required"); return;
    }
    setSaving(true);
    try {
      const { data } = await api.post(`/roles`, {
        label: creating.label.trim(),
        description: creating.description || "",
        permissions: creating.permissions || [],
        base_role: creating.base_role || "hospitality_coordinator",
      });
      toast.success(`Created '${data.label}'`);
      setCreating(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Create failed");
    } finally { setSaving(false); }
  };

  const deleteRole = async () => {
    if (!deleting) return;
    setSaving(true);
    try {
      await api.delete(`/roles/${deleting.key}`);
      toast.success(`Deleted '${deleting.label}'`);
      setDeleting(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    } finally { setSaving(false); }
  };

  const PermissionEditor = ({ value, onChange }) => (
    <div className="space-y-5 max-h-[55vh] overflow-y-auto pr-2 border border-[#E8E8E6] rounded-lg p-4 bg-[#FCFCFB]">
      {sections.map(([section, perms]) => {
        const sectionKeys = perms.map((p) => p.key);
        const checkedCount = sectionKeys.filter((k) => value.permissions.includes(k)).length;
        const allChecked = checkedCount === sectionKeys.length;
        return (
          <div key={section} className="space-y-2">
            <div className="flex items-center justify-between">
              <h4 className="font-semibold text-[#1B4332] text-sm uppercase tracking-wide">
                {section}
                <span className="ml-2 text-[#525C58] text-xs font-normal">
                  ({checkedCount}/{sectionKeys.length})
                </span>
              </h4>
              <button
                type="button"
                className="text-xs text-[#1B4332] hover:underline"
                onClick={() => onChange(toggleSection(value, sectionKeys, allChecked))}
                data-testid={`toggle-section-${section.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {allChecked ? "Unselect all" : "Select all"}
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {perms.map((p) => {
                const checked = value.permissions.includes(p.key);
                return (
                  <label
                    key={p.key}
                    className={`flex items-start gap-2 p-2 rounded border cursor-pointer transition ${
                      checked ? "bg-emerald-50 border-emerald-200" : "bg-white border-[#E8E8E6] hover:bg-[#F4F4F1]"
                    }`}
                    data-testid={`perm-${p.key}`}
                  >
                    <Checkbox
                      checked={checked}
                      onCheckedChange={() => onChange(togglePerm(value, p.key))}
                      className="mt-0.5"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-[#1C1F1E] leading-tight">{p.label}</div>
                      <div className="text-[10px] text-[#9AA39E] font-mono">{p.key}</div>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );

  return (
    <div className="space-y-6" data-testid="roles-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-[#1B4332] flex items-center gap-2">
            <ShieldAlert className="h-6 w-6" /> Roles &amp; permissions
          </h1>
          <p className="text-sm text-[#525C58] mt-1">
            Adjust the permissions of built-in roles or create your own custom roles. Custom roles can be assigned to any user.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load} data-testid="roles-refresh-btn">
            <RefreshCw className="h-4 w-4 mr-1" /> Refresh
          </Button>
          <Button
            size="sm"
            className="bg-[#1B4332] hover:bg-[#143526]"
            onClick={() => setCreating({ label: "", description: "", base_role: "hospitality_coordinator", permissions: [] })}
            data-testid="new-role-btn"
          >
            <Plus className="h-4 w-4 mr-1" /> New custom role
          </Button>
        </div>
      </div>

      {/* Roles list */}
      <Card>
        <CardHeader><CardTitle className="text-base">Roles in your organisation</CardTitle></CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-10 text-center text-[#9AA39E]" data-testid="roles-loading">Loading…</div>
          ) : (
            <div className="divide-y divide-[#E8E8E6]">
              {roles.map((r) => (
                <div key={r.key} className="px-6 py-4 flex items-center justify-between gap-4 hover:bg-[#F4F4F1] transition" data-testid={`role-row-${r.key}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-[#1B4332]">{r.label}</span>
                      {r.is_built_in ? (
                        <Badge variant="outline" className="text-[10px] bg-slate-100 text-slate-700 border-slate-200">
                          <Lock className="h-3 w-3 mr-1" /> Built-in
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-[10px] bg-amber-100 text-amber-800 border-amber-200">
                          <Unlock className="h-3 w-3 mr-1" /> Custom
                        </Badge>
                      )}
                      <Badge variant="outline" className="text-[10px]">
                        <Users className="h-3 w-3 mr-1" /> {r.user_count} {r.user_count === 1 ? "user" : "users"}
                      </Badge>
                      <Badge variant="outline" className="text-[10px] text-[#525C58]">
                        {(r.permissions || []).length} perms
                      </Badge>
                    </div>
                    <p className="text-xs text-[#525C58] mt-1 truncate">{r.description || <span className="text-[#9AA39E]">No description</span>}</p>
                    <p className="text-[10px] text-[#9AA39E] font-mono mt-0.5">{r.key}</p>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setEditing({ ...r, permissions: [...(r.permissions || [])] })}
                      data-testid={`edit-role-${r.key}`}
                    >
                      <Pencil className="h-4 w-4 mr-1" /> Edit
                    </Button>
                    {!r.is_built_in && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-red-600 hover:bg-red-50"
                        onClick={() => setDeleting(r)}
                        data-testid={`delete-role-${r.key}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle data-testid="edit-role-title">
              Edit role: {editing?.label}
              {editing?.is_built_in && <Badge variant="outline" className="ml-2 text-[10px]"><Lock className="h-3 w-3 mr-1" /> Built-in</Badge>}
            </DialogTitle>
            <DialogDescription>
              {editing?.is_built_in
                ? "Built-in roles can have their label, description and permissions adjusted, but the role key is fixed so existing users keep working."
                : "Edit this custom role's label, description and permissions."}
            </DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>Label</Label>
                  <Input
                    value={editing.label}
                    onChange={(e) => setEditing({ ...editing, label: e.target.value })}
                    data-testid="edit-role-label"
                  />
                </div>
                <div className="space-y-1">
                  <Label>Role key</Label>
                  <Input value={editing.key} disabled className="font-mono text-xs" />
                </div>
              </div>
              <div className="space-y-1">
                <Label>Description</Label>
                <Textarea
                  value={editing.description || ""}
                  onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                  rows={2}
                  data-testid="edit-role-description"
                />
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Permissions ({editing.permissions.length}/{catalog.length})</Label>
                  {editing.key === "admin" && (
                    <span className="text-xs text-amber-700">
                      ⚠ users.create, users.view & roles.manage are always retained on admin to prevent lockout.
                    </span>
                  )}
                </div>
                <PermissionEditor value={editing} onChange={setEditing} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={saveRole} disabled={saving} className="bg-[#1B4332] hover:bg-[#143526]" data-testid="save-role-btn">
              <Save className="h-4 w-4 mr-1" /> {saving ? "Saving…" : "Save changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create dialog */}
      <Dialog open={!!creating} onOpenChange={(open) => !open && setCreating(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Create custom role</DialogTitle>
            <DialogDescription>
              Pick a label, an inheritance base, and tick the permissions this role should grant.
            </DialogDescription>
          </DialogHeader>
          {creating && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>Label *</Label>
                  <Input
                    value={creating.label}
                    onChange={(e) => setCreating({ ...creating, label: e.target.value })}
                    placeholder="e.g. Front Desk"
                    data-testid="new-role-label"
                  />
                </div>
                <div className="space-y-1">
                  <Label>Inherits behaviour from</Label>
                  <Select
                    value={creating.base_role}
                    onValueChange={(v) => setCreating({ ...creating, base_role: v })}
                  >
                    <SelectTrigger data-testid="new-role-base"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="hospitality_coordinator">Hospitality Coordinator</SelectItem>
                      <SelectItem value="booking_coordinator">Booking Coordinator</SelectItem>
                      <SelectItem value="department_head">Department Head</SelectItem>
                      <SelectItem value="cmo_delegate">CMO / Delegate</SelectItem>
                      <SelectItem value="sending_agency">Sending Agency</SelectItem>
                      <SelectItem value="admin">Administrator</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-[#9AA39E]">For workflow gates that aren't yet permission-driven (e.g. department-level approvals).</p>
                </div>
              </div>
              <div className="space-y-1">
                <Label>Description</Label>
                <Textarea
                  value={creating.description || ""}
                  onChange={(e) => setCreating({ ...creating, description: e.target.value })}
                  rows={2}
                  placeholder="When should this role be assigned?"
                  data-testid="new-role-description"
                />
              </div>
              <div className="space-y-2">
                <Label>Permissions ({creating.permissions.length}/{catalog.length})</Label>
                <PermissionEditor value={creating} onChange={setCreating} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreating(null)}>Cancel</Button>
            <Button onClick={createRole} disabled={saving} className="bg-[#1B4332] hover:bg-[#143526]" data-testid="confirm-create-role">
              <Plus className="h-4 w-4 mr-1" /> {saving ? "Creating…" : "Create role"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!deleting} onOpenChange={(open) => !open && setDeleting(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete role</DialogTitle>
            <DialogDescription>
              Permanently remove the custom role "{deleting?.label}"? This cannot be undone. Users currently assigned this role must be reassigned first.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleting(null)}>Cancel</Button>
            <Button onClick={deleteRole} disabled={saving} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-role">
              <Trash2 className="h-4 w-4 mr-1" /> {saving ? "Deleting…" : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
